// Maps game concepts to sprite names from ../clay-farm-assets/MANIFEST.md.
//
// Every entry here is allowed to be missing. Art is generated in phases, so the
// renderer must run correctly with zero, some, or all of these present — that
// is the whole point of the fallback rule in AGENT.md §3.

import type { Breed, ChickenState, Gender, TerrainType } from '../core/types';

// Relative to wherever the app is mounted, not to the server root: on the
// tailnet the game lives under /clayfarm/ and another service owns /.
// BASE_URL always carries its trailing slash, so this is not concatenated with one.
export const SPRITE_ROOT = `${import.meta.env.BASE_URL}assets/sprites`;

/**
 * Naming: <category>_<name>[_<variant>][_<direction>][_f<frame>].webp
 *
 * Everything under assets/sprites is emitted by tools/refit-sprites.py, which
 * writes WebP — raw renders are 30x the bytes for art nobody sees at that size.
 */
export function spritePath(category: string, name: string): string {
  return `${SPRITE_ROOT}/${category}/${name}.webp`;
}

export const TILE_SPRITES: Record<TerrainType, string> = {
  grass: spritePath('tile', 'tile_grass'),
  // No dirt tile ships yet: the only render available was a hexagon, which
  // cannot tessellate a square grid. Falls back to the procedural tile until
  // the diamond version listed in gen-me.md is generated.
  dirt: spritePath('tile', 'tile_dirt'),
  wood_shavings: spritePath('tile', 'tile_shavings'),
  balcony_mat: spritePath('tile', 'tile_concrete'),
};

/** Breed key in asset naming — RIR is abbreviated in the manifest. */
const BREED_SLUG: Record<Breed, string> = {
  silkie: 'silkie',
  leghorn: 'leghorn',
  rhode_island_red: 'rir',
  australorp: 'australorp',
  isa_brown: 'isa_brown',
  brahma: 'brahma',
  plymouth_rock: 'plymouth_rock',
  buff_orpington: 'buff_orpington',
};

const STATE_CLIP: Record<ChickenState, string> = {
  IDLE: 'idle',
  WALKING: 'walk',
  PECKING: 'peck',
  ROOSTING: 'sleep',
  BROODY: 'sleep',
  ESCAPED: 'walk',
};

/**
 * Frames per animation clip, per ART-LAW section 8: 12fps stop-motion, 2-3
 * frames per pose. 6-9 frame cycles are forbidden, so there is no single
 * constant. Every number below is quoted from the Law 8 pose table, except
 * sleep, which the Law does not list: the engine requests it for
 * roosting/broody birds and a roosting bird is a still, so it is 1.
 */
export const CLIP_FRAMES: Record<string, Record<string, number>> = {
  hen: { idle: 2, peck: 2, walk: 3, sleep: 1 },
  pullet: { idle: 2, walk: 3 },
  rooster: { idle: 2, walk: 3, crow: 2 },
  chick: { idle: 2, walk: 2, cheep: 1 },
};

/**
 * Chick idle frames per breed. The owner passed the previously rejected Buff
 * Orpington and Leghorn chick idle frames for ship on 2026-09-30 ("better
 * than the placeholder images", improvement to follow), so all five breeds
 * with chick art now have 2 frames each. The remaining breeds have no chick
 * art at all. A zero count means the renderer must not reference clip files
 * for that breed's chicks -- it falls back to the reference pose. No
 * placeholder art is invented.
 */
export const CHICK_IDLE_FRAMES: Record<Breed, number> = {
  australorp: 2,
  buff_orpington: 2,
  leghorn: 2,
  rhode_island_red: 2,
  silkie: 2,
  isa_brown: 0,
  brahma: 0,
  plymouth_rock: 0,
};

/**
 * Breeds whose adult art shipped as `_v2` canonical files (round 3, approved
 * 2026-09-30): isa_brown, brahma, plymouth_rock. The sync pipeline never
 * renames files, so these resolve to
 * canonical/chicken_<slug>_<clip>_<dir>_v2[_f<NN>].webp — first frame carries
 * no _fNN token, frames 2+ carry _f02/_f03 — instead of the
 * chicken/chicken_<slug>_<clip>_<dir>_f<NN>.webp convention the other breeds
 * use. Frame counts follow the hen row of CLIP_FRAMES (idle 2 / walk 3 /
 * peck 2 / sleep 1).
 */
export const V2_CANONICAL_BREEDS: ReadonlySet<Breed> = new Set([
  'isa_brown',
  'brahma',
  'plymouth_rock',
]);

export type ClipStage = keyof typeof CLIP_FRAMES;

/**
 * Frame count for one clip. A clip ART-LAW section 8 does not list resolves
 * to 1 (a still), never to a multi-frame cycle.
 */
export function clipFrameCount(stage: ClipStage, clip: string): number {
  return CLIP_FRAMES[stage][clip] ?? CLIP_FRAMES.hen[clip] ?? 1;
}

/**
 * Frame count for the clip the engine plays for this bird right now.
 * Pullets and roosters resolve to the hen counts ("as hen", ART-LAW section 8)
 * for the clips the engine requests (idle/walk/peck/sleep).
 * Chick idle is breed-aware (CHICK_IDLE_FRAMES): breeds with no approved
 * frames get 0, so no clip file is ever referenced for them.
 */
export function clipFramesFor(
  state: ChickenState,
  isChick: boolean,
  breed?: Breed,
): number {
  if (isChick && breed && STATE_CLIP[state] === "idle") {
    return CHICK_IDLE_FRAMES[breed] ?? 0;
  }
  return clipFrameCount(isChick ? "chick" : "hen", STATE_CLIP[state]);
}

/**
 * Animation frame path. Only front-right and back-right are generated — left
 * views are engine-mirrored, so `facing` never asks for a left sprite.
 * Chick idle is breed-aware: breeds with shipped chick art resolve to the
 * breed-specific frames in canonical/
 * (chicken_chick_<slug>_idle_fr_v2_f<NN>.webp); other chick clips keep the
 * legacy generic chick_baby_* path. Adults of the V2_CANONICAL_BREEDS resolve
 * to their _v2 canonical files. Returns null when the breed has no approved
 * frames for the clip: there is no file to reference, so the caller must fall
 * back to the reference pose without requesting anything.
 */
export function chickenFramePath(
  breed: Breed,
  state: ChickenState,
  frame: number,
  facing: 'fr' | 'br' = 'fr',
  isChick = false,
): string | null {
  const clip = STATE_CLIP[state];
  const count = clipFramesFor(state, isChick, breed);
  if (count === 0) return null;
  const index = String((frame % count) + 1).padStart(2, '0');
  if (isChick) {
    if (clip === 'idle' && (CHICK_IDLE_FRAMES[breed] ?? 0) > 0) {
      return spritePath(
        'canonical',
        `chicken_chick_${BREED_SLUG[breed]}_${clip}_${facing}_v2_f${index}`,
      );
    }
    return spritePath('chick', `chick_baby_${clip}_${facing}_f${index}`);
  }
  if (V2_CANONICAL_BREEDS.has(breed)) {
    const frameSuffix = index === '01' ? '' : `_f${index}`;
    return spritePath(
      'canonical',
      `chicken_${BREED_SLUG[breed]}_${clip}_${facing}_v2${frameSuffix}`,
    );
  }
  return spritePath('chicken', `chicken_${BREED_SLUG[breed]}_${clip}_${facing}_f${index}`);
}

/**
 * Canonical reference art — the only chicken sprites that exist right now.
 *
 * Only one rooster pose was rendered, so every breed's cockerel shares it. That
 * is still better than the alternative: a rooster drawn as a hen contradicts
 * the flock panel, which names him one, and the noise model, which charges the
 * player for him. Per-breed roosters are listed in gen-me.md.
 */
export function chickenReferencePath(
  breed: Breed,
  isChick = false,
  gender: Gender = 'hen',
): string {
  if (isChick) return spritePath('canonical', 'chick_baby_ref_fr');
  if (gender === 'rooster') return spritePath('canonical', 'chicken_rooster_ref_fr');
  if (V2_CANONICAL_BREEDS.has(breed)) {
    return spritePath('canonical', `chicken_${BREED_SLUG[breed]}_ref_fr_v2`);
  }
  return spritePath('canonical', `chicken_${BREED_SLUG[breed]}_ref_fr`);
}

/**
 * Structure sprites keyed by store item id. Every placeable item has an entry,
 * so art can land without a code change — a missing file is already handled.
 *
 * Items only share a sprite when they are the same object. They must never
 * share one across different footprints: the renderer scales a structure to its
 * footprint width, so a 3x2 coop drawn from the 2x2 sprite comes out stretched.
 * They also must not share across a difference the player is meant to learn
 * (a chick fount is not an adult hopper) — this is a husbandry game.
 */
export const STRUCTURE_SPRITES: Record<string, string> = {
  coop_l1: spritePath('struct', 'struct_coop_backyard'),
  coop_l2: spritePath('struct', 'struct_coop_l2'),
  coop_master: spritePath('struct', 'struct_coop_master'),
  quarantine_pen: spritePath('struct', 'struct_quarantine_pen'),
  nesting_box: spritePath('struct', 'struct_nestbox'),
  perch_bar: spritePath('struct', 'struct_perch'),
  brooder_box: spritePath('struct', 'struct_brooder'),
  coop_light: spritePath('struct', 'struct_coop_light'),
  chick_waterer: spritePath('struct', 'struct_water_fount_chick'),
  adult_waterer: spritePath('struct', 'struct_water_hopper'),
  chick_feeder: spritePath('struct', 'struct_feed_tray_chick'),
  adult_feeder: spritePath('struct', 'struct_feed_dish'),
  covered_feeder: spritePath('struct', 'struct_feeder_covered'),
  footbath: spritePath('struct', 'struct_footbath'),
  water_barrel: spritePath('struct', 'struct_water_barrel'),
   shade_sail: spritePath('struct', 'struct_shade_sail'),
   run_netting: spritePath('struct', 'struct_run_netting'),
   hardware_cloth: spritePath('struct', 'struct_hardware_cloth'),
   decor_flag: spritePath('struct', 'struct_decor_flag'),
   decor_planter: spritePath('struct', 'struct_decor_planter'),
   auto_feeder: spritePath('struct', 'struct_auto_feeder_b_fr_v2'),
   auto_waterer: spritePath('struct', 'struct_auto_waterer'),
   coop_lock_timer: spritePath('struct', 'struct_coop_lock_timer'),
};

/**
 * Height override for structure art, in tile-depth units.
 *
 * Renders arrive framed to fill their canvas, so real-world scale is thrown
 * away — a feed dish and a coop both come back 512px tall. Scaling by footprint
 * width alone cannot recover it, and the first batch put a drinker taller than
 * the coop beside it. Listed items are fitted to this height as well as to
 * their footprint width, whichever is the tighter constraint.
 *
 * Only items observed to come out wrong are listed. A render framed to true
 * relative scale needs no entry — see gen-me.md §2.
 */
export const STRUCTURE_HEIGHT_UNITS: Record<string, number> = {
  adult_feeder: 1.0,
  adult_waterer: 2.2,
  nesting_box: 2.6,
  perch_bar: 3.0,
};

/**
 * R5 wardrobe: cosmetic drop id → shipped `_fr` art. Same-canvas renders, so
 * the overlay blits at (0,0) with the body's own anchor/scale (DROP-ASSETS).
 * Unmapped ids have no art yet and render nothing — honest, not broken.
 */
const COSMETIC_ART: Record<string, string> = {
  straw_hat: 'cosmetic_straw_hat_fr',
  pava: 'cosmetic_pava_fr',
  party_cone: 'cosmetic_party_hat_fr',
  bow_tie: 'cosmetic_bowtie_red_fr',
  bandana_red: 'cosmetic_pirate_bandana_fr',
  flor_de_maga: 'cosmetic_flower_crown_fr',
  rain_cape: 'cosmetic_royal_cape_fr',
  knit_sweater: 'cosmetic_safety_vest_fr',
};

export function cosmeticPath(id: string): string | null {
  const art = COSMETIC_ART[id];
  return art ? spritePath('cosmetic', art) : null;
}

export const FX_SPRITES = {  raindrop: spritePath('fx', 'fx_raindrop'),
  puddle: spritePath('fx', 'fx_puddle'),
  fogWisp: spritePath('fx', 'fx_fog_wisp'),
  sunRay: spritePath('fx', 'fx_sun_ray'),
  windowGlow: spritePath('fx', 'fx_window_glow'),
  cloudShadow: spritePath('fx', 'fx_cloud_shadow'),
} as const;

export const ICON_SPRITES = {
  eggit: spritePath('icon', 'icon_eggit'),
  egg: spritePath('icon', 'icon_egg'),
  water: spritePath('icon', 'icon_water'),
  feed: spritePath('icon', 'icon_feed'),
  broom: spritePath('icon', 'icon_broom'),
  heart: spritePath('icon', 'icon_heart'),
  brooder: spritePath('icon', 'icon_brooder'),
  padlock: spritePath('icon', 'icon_padlock'),
} as const;

export type IconKey = keyof typeof ICON_SPRITES;

/**
 * Icons whose file actually exists today.
 *
 * The DOM UI cannot fall back the way the renderer does — a missing `<img>`
 * leaves a broken-image box rather than a primitive — so the emoji fallback has
 * to be chosen before the request is made. `iconSprites.test.ts` checks this
 * list against the files on disk, so adding art is a one-line change here and
 * forgetting to add it fails a test rather than shipping a broken box.
 */
export const SHIPPED_ICONS: ReadonlySet<IconKey> = new Set<IconKey>([
  'eggit',
  'egg',
  'water',
  'feed',
  'broom',
  'heart',
  'brooder',
  'padlock',
]);

/**
 * Everything worth warming at boot — misses are expected and harmless.
 *
 * Structures are deliberately absent. None of their art exists yet, so
 * preloading them costs a round of 404s on every load for sprites the player
 * may never place; `getOrRequest` fetches each one the first time it is needed.
 */
export function preloadList(): string[] {
  return [
    ...Object.values(TILE_SPRITES),
    ...(['silkie', 'leghorn', 'rhode_island_red', 'australorp', 'isa_brown', 'brahma', 'plymouth_rock', 'buff_orpington'] as Breed[]).map((b) =>
      chickenReferencePath(b),
    ),
    chickenReferencePath('silkie', false, 'rooster'),
    chickenReferencePath('silkie', true),
  ];
}
